import { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';

const DEFAULT_PAYLOAD = {
  avatar: {
    age: "middle",
    gender: "female",
    ethnicity: "light",
    hospital: "University Gastroenterology",
    language: "english"
  },
  data: {
    weekday: "Monday",
    time: "afternoon",
    concentrate: "miralax",
    questionnaire: ["bloodThinners", "diabetesMedicines"]
  },
  project: { name: "UGI Video 01" }
};

const AVATAR_OPTIONS = {
  gender:    ["male", "female"],
  age:       ["young", "middle", "old"],
  ethnicity: ["light", "dark"],
  language:  ["english", "hebrew", "spanish"],
};

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const TIMES = ["afternoon", "evening"];
const CONCENTRATE_OPTIONS = ["miralax", "suprep", "clenpiq", "moviprep"];
const QUESTIONNAIRE_KEYS = ["bloodThinners", "diabetesMedicines", "bloodPressureMedicines", "nutritionalSupplement"];
const PROJECTS = ["UGI Video 01", "UGI Video 02"];

export default function AdminVideoTestPage() {
  const [form, setForm] = useState({
    gender: 'female', age: 'middle', ethnicity: 'light',
    hospital: 'University Gastroenterology', language: 'english',
    project: 'UGI Video 01',
    weekday: 'Monday', time: 'afternoon', concentrate: 'miralax',
    questionnaire: ['bloodThinners', 'diabetesMedicines'],
    doctorName: '',
  });
  const [jsonText, setJsonText] = useState(JSON.stringify(DEFAULT_PAYLOAD, null, 2));
  const [jsonMode, setJsonMode] = useState(false); // false = form, true = raw JSON
  const [jsonError, setJsonError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [history, setHistory] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [rawResponse, setRawResponse] = useState(null);
  const videoRef = useRef(null);

  useEffect(() => {
    axios.get('/api/gi/doctors')
      .then(r => setDoctors(r.data || []))
      .catch(() => {});
  }, []);

  const formToPayload = (f) => ({
    avatar: { age: f.age, gender: f.gender, ethnicity: f.ethnicity, hospital: f.hospital, language: f.language },
    data: {
      weekday: f.weekday,
      time: f.time,
      concentrate: f.concentrate || undefined,
      questionnaire: f.questionnaire,
      ...(f.doctorName && { doctorName: f.doctorName }),
    },
    project: { name: f.project },
  });

  const updateForm = (key, val) => {
    const updated = { ...form, [key]: val };
    setForm(updated);
    if (!jsonMode) setJsonText(JSON.stringify(formToPayload(updated), null, 2));
  };

  const toggleQuestionnaire = (key) => {
    const updated = form.questionnaire.includes(key)
      ? form.questionnaire.filter(k => k !== key)
      : [...form.questionnaire, key];
    updateForm('questionnaire', updated);
  };

  const handleGenerate = async () => {
    setJsonError(null);
    setApiError(null);
    setResult(null);

    let payload;
    try {
      payload = JSON.parse(jsonText);
    } catch (e) {
      setJsonError(`Invalid JSON: ${e.message}`);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        'https://animator.gistmd.com/api/video/v1/requestLink',
        payload
      );
      setResult(res.data);
      setRawResponse(res.data);
      setHistory(prev => [{
        ts: new Date().toLocaleTimeString(),
        avatar: payload.avatar,
        project: payload.project?.name,
        src: res.data?.src,
        status: 'success'
      }, ...prev.slice(0, 9)]);
      setTimeout(() => videoRef.current?.play(), 300);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.response?.data || err.message;
      setApiError(typeof msg === 'object' ? JSON.stringify(msg) : msg);
      setRawResponse(err?.response?.data || { error: msg });
      setHistory(prev => [{
        ts: new Date().toLocaleTimeString(),
        avatar: payload.avatar,
        project: payload.project?.name,
        src: null,
        status: 'error',
        error: typeof msg === 'object' ? JSON.stringify(msg) : msg
      }, ...prev.slice(0, 9)]);
    } finally {
      setLoading(false);
    }
  };


  return (
    <Page>
      <PageTitle>Video Generator Test</PageTitle>
      <PageDesc>Send a payload directly to the animator service and preview the generated video.</PageDesc>

      <Layout>
        <LeftPanel>

          <ModeToggle>
            <ModeBtn $active={!jsonMode} onClick={() => setJsonMode(false)}>Form Builder</ModeBtn>
            <ModeBtn $active={jsonMode} onClick={() => { setJsonMode(true); }}>Raw JSON</ModeBtn>
          </ModeToggle>

          {!jsonMode && (
            <FormBuilder>
              <FormGroup>
                <FormGroupTitle>Project</FormGroupTitle>
                <ChipRow>
                  {PROJECTS.map(p => (
                    <Chip key={p} $active={form.project === p} onClick={() => updateForm('project', p)}>{p}</Chip>
                  ))}
                </ChipRow>
              </FormGroup>

              <FormGroup>
                <FormGroupTitle>Avatar</FormGroupTitle>
                {Object.entries(AVATAR_OPTIONS).map(([key, opts]) => (
                  <FormRow key={key}>
                    <FormKey>{key}</FormKey>
                    <ChipRow>
                      {opts.map(o => (
                        <Chip key={o} $active={form[key] === o} onClick={() => updateForm(key, o)}>{o}</Chip>
                      ))}
                    </ChipRow>
                  </FormRow>
                ))}
                <FormRow>
                  <FormKey>hospital</FormKey>
                  <FormInput
                    value={form.hospital}
                    onChange={e => updateForm('hospital', e.target.value)}
                    placeholder="Hospital name"
                  />
                </FormRow>
              </FormGroup>

              <FormGroup>
                <FormGroupTitle>Data</FormGroupTitle>
                <FormRow>
                  <FormKey>weekday</FormKey>
                  <ChipRow style={{ flexWrap: 'wrap' }}>
                    {WEEKDAYS.map(w => (
                      <Chip key={w} $active={form.weekday === w} onClick={() => updateForm('weekday', w)}>{w.slice(0,3)}</Chip>
                    ))}
                  </ChipRow>
                </FormRow>
                <FormRow>
                  <FormKey>time</FormKey>
                  <ChipRow>
                    {TIMES.map(t => (
                      <Chip key={t} $active={form.time === t} onClick={() => updateForm('time', t)}>{t}</Chip>
                    ))}
                  </ChipRow>
                </FormRow>
                <FormRow>
                  <FormKey>concentrate</FormKey>
                  <ChipRow style={{ flexWrap: 'wrap' }}>
                    {CONCENTRATE_OPTIONS.map(c => (
                      <Chip key={c} $active={form.concentrate === c} onClick={() => updateForm('concentrate', c)}>{c}</Chip>
                    ))}
                  </ChipRow>
                </FormRow>
                <FormRow>
                  <FormKey>doctor</FormKey>
                  <FormSelect
                    value={form.doctorName}
                    onChange={e => updateForm('doctorName', e.target.value)}
                  >
                    <option value="">— none —</option>
                    {doctors.map(d => (
                      <option key={d.id} value={d.name}>{d.name}</option>
                    ))}
                  </FormSelect>
                </FormRow>
              </FormGroup>

              <FormGroup>
                <FormGroupTitle>Questionnaire (medications answered yes)</FormGroupTitle>
                <CheckList>
                  {QUESTIONNAIRE_KEYS.map(k => (
                    <CheckItem key={k} onClick={() => toggleQuestionnaire(k)}>
                      <Checkbox $checked={form.questionnaire.includes(k)} />
                      {k}
                    </CheckItem>
                  ))}
                </CheckList>
              </FormGroup>
            </FormBuilder>
          )}

          <SectionLabel style={{ marginTop: '1rem' }}>
            {jsonMode ? 'Raw JSON Payload' : 'Generated Payload'}
            {jsonError && <JsonErrBadge>{jsonError}</JsonErrBadge>}
          </SectionLabel>
          <JsonEditor
            value={jsonText}
            onChange={e => { setJsonText(e.target.value); setJsonError(null); }}
            spellCheck={false}
            $error={!!jsonError}
            readOnly={!jsonMode}
          />

          <GenerateBtn onClick={handleGenerate} disabled={loading}>
            {loading ? 'Generating…' : '▶ Generate Video'}
          </GenerateBtn>

          {apiError && <ApiError>❌ {apiError}</ApiError>}

          {history.length > 0 && (
            <>
              <SectionLabel style={{ marginTop: '1.5rem' }}>Recent Requests</SectionLabel>
              {history.map((h, i) => (
                <HistoryItem key={i} $success={h.status === 'success'}>
                  <HistoryMeta>
                    <span>{h.ts}</span>
                    <span>{h.avatar?.gender} / {h.avatar?.age} / {h.avatar?.ethnicity}</span>
                    <span>{h.project}</span>
                  </HistoryMeta>
                  {h.status === 'success'
                    ? <HistoryLink onClick={() => { setResult({ src: h.src }); setTimeout(() => videoRef.current?.load(), 100); }}>▶ Play again</HistoryLink>
                    : <HistoryErr>{h.error}</HistoryErr>
                  }
                </HistoryItem>
              ))}
            </>
          )}
        </LeftPanel>

        <RightPanel>
          <SectionLabel>Video Preview</SectionLabel>
          {!result && !loading && (
            <EmptyPreview>
              <EmptyIcon>🎬</EmptyIcon>
              <EmptyText>Enter a payload and click Generate Video</EmptyText>
            </EmptyPreview>
          )}
          {loading && (
            <EmptyPreview>
              <Spinner />
              <EmptyText>Requesting video from animator…</EmptyText>
            </EmptyPreview>
          )}
          {result?.src && (
            <>
              <VideoPlayer
                ref={videoRef}
                src={result.src}
                controls
                autoPlay
                playsInline
              />
              <VideoMeta>
                <MetaRow><MetaKey>URL</MetaKey><MetaVal><a href={result.src} target="_blank" rel="noreferrer">{result.src}</a></MetaVal></MetaRow>
                <MetaRow><MetaKey>Copy</MetaKey><MetaVal><CopyBtn onClick={() => navigator.clipboard.writeText(result.src)}>Copy URL</CopyBtn></MetaVal></MetaRow>
              </VideoMeta>
            </>
          )}

          <ResponseSection style={{ marginTop: result?.src ? '1rem' : '0' }}>
            <SectionLabel>
              API Response
              {rawResponse && <RawBadge $ok={!!result?.src}>{result?.src ? 'success' : 'error'}</RawBadge>}
            </SectionLabel>
            <ResponsePre>
              {rawResponse ? JSON.stringify(rawResponse, null, 2) : '// response will appear here after Generate'}
            </ResponsePre>
          </ResponseSection>
        </RightPanel>
      </Layout>
    </Page>
  );
}

const Page = styled.div`font-family: 'Poppins', sans-serif; color: #1a1a1a;`;
const PageTitle = styled.h1`font-size: 1.8rem; color: #333; margin: 0 0 0.5rem;`;
const PageDesc = styled.p`font-size: 0.9rem; color: #999; margin: 0 0 2rem;`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 420px 1fr;
  gap: 1.5rem;
  align-items: start;
`;

const LeftPanel = styled.div`display: flex; flex-direction: column;`;
const RightPanel = styled.div`display: flex; flex-direction: column;`;

const SectionLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #999;
  margin-bottom: 0.6rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const FormSelect = styled.select`
  flex: 1; padding: 0.35rem 0.6rem; border: 1px solid #e5e5e5;
  border-radius: 6px; font-size: 0.82rem; color: #333;
  &:focus { outline: none; border-color: #84a4fc; }
`;

const RawBadge = styled.span`
  padding: 0.15rem 0.5rem; border-radius: 10px; font-size: 0.72rem;
  font-weight: 700; text-transform: none;
  background: ${p => p.$ok ? '#e8f8f0' : '#fff0f3'};
  color: ${p => p.$ok ? '#27ae60' : '#f02a4c'};
`;

const JsonEditor = styled.textarea`
  width: 100%;
  min-height: 300px;
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 0.8rem;
  padding: 1rem;
  border: 1px solid ${p => p.$error ? '#f02a4c' : '#e5e5e5'};
  border-radius: 8px;
  background: ${p => p.$error ? '#fff5f7' : '#fafafa'};
  resize: vertical;
  line-height: 1.6;
  color: #333;
  box-sizing: border-box;
  &:focus { outline: none; border-color: #84a4fc; }
`;

const JsonErrBadge = styled.span`
  background: #fff0f3; color: #f02a4c;
  padding: 0.2rem 0.6rem; border-radius: 6px;
  font-size: 0.75rem; font-weight: 600; text-transform: none;
`;

const GenerateBtn = styled.button`
  margin-top: 1rem;
  padding: 0.85rem;
  background: ${p => p.disabled ? '#ccc' : '#f02a4c'};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: ${p => p.disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.2s;
  &:hover:not(:disabled) { background: #d4223f; }
`;

const ApiError = styled.div`
  margin-top: 0.75rem;
  background: #fff5f7;
  border: 1px solid #f02a4c;
  color: #f02a4c;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.82rem;
  word-break: break-all;
`;

const HistoryItem = styled.div`
  padding: 0.6rem 0.75rem;
  border-radius: 8px;
  border-left: 3px solid ${p => p.$success ? '#27ae60' : '#f02a4c'};
  background: white;
  margin-bottom: 0.4rem;
`;
const HistoryMeta = styled.div`
  display: flex; gap: 0.75rem; font-size: 0.75rem; color: #999; margin-bottom: 0.3rem;
`;
const HistoryLink = styled.span`
  font-size: 0.8rem; color: #84a4fc; cursor: pointer; font-weight: 600;
  &:hover { text-decoration: underline; }
`;
const HistoryErr = styled.div`font-size: 0.75rem; color: #f02a4c;`;

const EmptyPreview = styled.div`
  background: white;
  border-radius: 12px;
  min-height: 340px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
`;
const EmptyIcon = styled.div`font-size: 3rem;`;
const EmptyText = styled.div`font-size: 0.9rem; color: #999;`;

const Spinner = styled.div`
  width: 40px; height: 40px;
  border: 3px solid #f0f0f0;
  border-top-color: #f02a4c;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;

const VideoPlayer = styled.video`
  width: 100%;
  border-radius: 12px;
  background: #000;
  box-shadow: 0 2px 16px rgba(0,0,0,0.15);
`;

const VideoMeta = styled.div`
  background: white;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-top: 0.75rem;
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
`;
const MetaRow = styled.div`display: flex; align-items: baseline; gap: 0.75rem; font-size: 0.82rem; margin-bottom: 0.35rem; &:last-child { margin: 0; }`;
const MetaKey = styled.span`font-weight: 700; color: #999; min-width: 50px; font-size: 0.75rem; text-transform: uppercase;`;
const MetaVal = styled.span`color: #333; word-break: break-all; a { color: #84a4fc; }`;
const CopyBtn = styled.button`
  padding: 0.25rem 0.75rem; background: #f0f4ff; border: none; border-radius: 6px;
  color: #84a4fc; font-size: 0.78rem; font-weight: 600; cursor: pointer;
  &:hover { background: #e0eaff; }
`;

const ResponseSection = styled.div`margin-top: 1rem;`;
const ResponsePre = styled.pre`
  background: #1a1a2e; color: #a8d8a8;
  padding: 1rem; border-radius: 8px;
  font-size: 0.78rem; overflow-x: auto;
  max-height: 200px; overflow-y: auto;
  margin: 0;
`;

const ModeToggle = styled.div`
  display: flex; gap: 0; margin-bottom: 1.25rem;
  border: 1px solid #e5e5e5; border-radius: 8px; overflow: hidden;
`;
const ModeBtn = styled.button`
  flex: 1; padding: 0.5rem; border: none;
  background: ${p => p.$active ? '#f02a4c' : 'white'};
  color: ${p => p.$active ? 'white' : '#666'};
  font-size: 0.82rem; font-weight: 600; cursor: pointer;
  transition: all 0.15s;
  &:hover { background: ${p => p.$active ? '#d4223f' : '#f5f5f5'}; }
`;

const FormBuilder = styled.div`display: flex; flex-direction: column; gap: 0.75rem; margin-bottom: 0.5rem;`;

const FormGroup = styled.div`
  background: white; border-radius: 10px;
  padding: 0.9rem 1rem; box-shadow: 0 1px 4px rgba(0,0,0,0.06);
`;
const FormGroupTitle = styled.div`
  font-size: 0.72rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.5px; color: #999; margin-bottom: 0.6rem;
`;
const FormRow = styled.div`display: flex; align-items: center; gap: 0.75rem; margin-bottom: 0.5rem; &:last-child { margin: 0; }`;
const FormKey = styled.div`font-size: 0.78rem; color: #666; min-width: 80px; font-weight: 500;`;
const FormInput = styled.input`
  flex: 1; padding: 0.35rem 0.6rem; border: 1px solid #e5e5e5;
  border-radius: 6px; font-size: 0.82rem; color: #333;
  &:focus { outline: none; border-color: #84a4fc; }
`;

const ChipRow = styled.div`display: flex; gap: 0.35rem; flex-wrap: wrap;`;
const Chip = styled.button`
  padding: 0.25rem 0.6rem; border-radius: 20px; font-size: 0.78rem;
  font-weight: 500; cursor: pointer; transition: all 0.12s; border: 1px solid;
  background: ${p => p.$active ? '#f02a4c' : 'white'};
  color: ${p => p.$active ? 'white' : '#666'};
  border-color: ${p => p.$active ? '#f02a4c' : '#e5e5e5'};
  &:hover { border-color: #f02a4c; color: ${p => p.$active ? 'white' : '#f02a4c'}; }
`;

const CheckList = styled.div`display: flex; flex-direction: column; gap: 0.4rem;`;
const CheckItem = styled.div`
  display: flex; align-items: center; gap: 0.6rem;
  font-size: 0.82rem; color: #444; cursor: pointer;
  padding: 0.25rem 0.35rem; border-radius: 6px;
  &:hover { background: #f9f9f9; }
`;
const Checkbox = styled.div`
  width: 16px; height: 16px; border-radius: 4px; flex-shrink: 0;
  border: 2px solid ${p => p.$checked ? '#f02a4c' : '#ccc'};
  background: ${p => p.$checked ? '#f02a4c' : 'white'};
  display: flex; align-items: center; justify-content: center;
  &::after { content: '${p => p.$checked ? '✓' : ''}'; color: white; font-size: 10px; font-weight: 700; }
`;
